import React, { useState, useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, LineElement, PointElement, BarElement,
  LinearScale, CategoryScale, Filler, Tooltip,
} from 'chart.js';
import { useApp } from '../../context/AppContext';
import { formatCurrency, shortMonths } from '../../utils/formatters';
import { toBRL } from '../../utils/exchange';

ChartJS.register(LineElement, PointElement, BarElement, LinearScale, CategoryScale, Filler, Tooltip);

const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return `${shortMonths[parseInt(m, 10) - 1]}/${y.slice(2)}`;
};

const axisOpts = {
  x: { grid: { display: false }, ticks: { font: { size: 10 }, color: 'rgba(128,128,128,0.7)', maxRotation: 0 } },
  y: {
    grid: { color: 'rgba(128,128,128,0.08)' },
    ticks: {
      font: { size: 10 }, color: 'rgba(128,128,128,0.7)',
      callback: v => Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0),
    },
  },
};

// ── Evolução do Patrimônio ──────────────────────────────────────────────
// Linha do valor atual da carteira mês a mês (fotografias em investHistory),
// com o total investido tracejado por baixo — o vão entre as duas é o lucro.
export function PortfolioEvolutionChart() {
  const { state } = useApp();
  const history = [...(state.investHistory || [])].sort((a, b) => a.month.localeCompare(b.month)).slice(-24);

  if (state.investments.length === 0) return null;

  if (history.length < 2) {
    return (
      <div className="card" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(55,138,221,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="fas fa-chart-line" style={{ color: 'var(--blue)', fontSize: 15 }}></i>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          <strong style={{ color: 'var(--text)' }}>Evolução do Patrimônio:</strong> comecei a fotografar sua carteira todo mês.
          A partir do mês que vem, o gráfico da evolução aparece aqui.
        </div>
      </div>
    );
  }

  const first = history[0];
  const last = history[history.length - 1];
  const diff = last.current - first.current;
  const positive = diff >= 0;
  const trendColor = positive ? '#1d9e75' : '#e24b4a';

  const data = {
    labels: history.map(h => monthLabel(h.month)),
    datasets: [
      {
        data: history.map(h => h.current),
        borderColor: trendColor,
        backgroundColor: positive ? 'rgba(29,158,117,0.08)' : 'rgba(226,75,74,0.08)',
        borderWidth: 2.5, fill: true, tension: 0.35,
        pointBackgroundColor: trendColor, pointBorderColor: '#fff', pointBorderWidth: 2,
        pointRadius: (ctx) => (ctx.dataIndex === 0 || ctx.dataIndex === history.length - 1) ? 4 : 2.5,
      },
      {
        data: history.map(h => h.invested),
        borderColor: 'rgba(128,128,128,0.55)',
        borderWidth: 1.5, borderDash: [5, 4], fill: false, tension: 0.35, pointRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => ` ${ctx.datasetIndex === 0 ? 'Carteira' : 'Investido'}: ${formatCurrency(ctx.raw)}` } },
    },
    scales: axisOpts,
  };

  return (
    <div className="card" style={{ marginBottom: 12, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: positive ? '#d1fae5' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-chart-line" style={{ color: trendColor, fontSize: 15 }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Evolução do Patrimônio</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              <span style={{ borderBottom: `2px solid ${trendColor}`, marginRight: 10 }}>carteira</span>
              <span style={{ borderBottom: '2px dashed rgba(128,128,128,0.55)' }}>total investido</span>
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Desde {monthLabel(first.month)}</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: trendColor }}>{positive ? '+' : ''}{formatCurrency(diff)}</div>
        </div>
      </div>
      <div style={{ height: 170 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
}

// ── Proventos por mês ───────────────────────────────────────────────────
// Barras dos últimos 12 meses somando as transações de Dividendo/JCP/Rendimento
// (criadas pelos botões da tela de Investimentos e pela confirmação de proventos).
export function DividendsChart() {
  const { state } = useApp();
  const [ativo, setAtivo] = useState('Todos');

  // Entradas de provento menos o imposto retido na fonte — o gráfico mostra o LÍQUIDO
  const divTxs = useMemo(() =>
    state.transactions.filter(t =>
      (t.type === 'income' && t.categoryId === 'inv' && /^(Dividendo|JCP|Rendimento)/.test(t.description || '')) ||
      (t.type === 'expense' && /^Imposto retido — /.test(t.description || ''))
    ), [state.transactions]);

  const ativos = useMemo(() => {
    const s = new Set();
    divTxs.forEach(t => { const n = (t.description || '').split(' — ')[1]; if (n) s.add(n); });
    return [...s].sort();
  }, [divTxs]);

  if (divTxs.length === 0) return null;

  const now = new Date();
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const filtered = ativo === 'Todos' ? divTxs : divTxs.filter(t => (t.description || '').endsWith(`— ${ativo}`));
  const byMonth = months.map(m =>
    Math.round(filtered.filter(t => t.date?.startsWith(m))
      .reduce((s, t) => s + (t.type === 'expense' ? -1 : 1) * toBRL(t.value, t.currency, state.usdRate), 0) * 100) / 100
  );
  const total12m = byMonth.reduce((s, v) => s + v, 0);

  const data = {
    labels: months.map(monthLabel),
    datasets: [{
      data: byMonth,
      backgroundColor: 'rgba(201,162,39,0.75)',
      hoverBackgroundColor: '#c9a227',
      borderRadius: 5,
      maxBarThickness: 26,
    }],
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => ` ${formatCurrency(ctx.raw)}` } },
    },
    scales: axisOpts,
  };

  return (
    <div className="card" style={{ marginBottom: 12, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(201,162,39,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-coins" style={{ color: 'var(--gold)', fontSize: 15 }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Proventos por mês</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Dividendos, JCP e rendimentos — últimos 12 meses</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {ativos.length > 1 && (
            <select className="form-control" value={ativo} onChange={e => setAtivo(e.target.value)} style={{ width: 'auto', padding: '5px 10px', fontSize: 12 }}>
              <option value="Todos">Todos os ativos</option>
              {ativos.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Total 12 meses</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--gold)' }}>{formatCurrency(total12m)}</div>
          </div>
        </div>
      </div>
      <div style={{ height: 150 }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
}
